import { useCallback, useEffect, useRef } from 'react';
import {
  FieldValues,
  // eslint-disable-next-line no-restricted-imports
  useForm as useFormRHF,
  // eslint-disable-next-line no-restricted-imports
  useFormContext as useFormContextRHF,
  UseFormProps,
} from 'react-hook-form';

import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { UseFormReturn } from '@/types/react-hook-form';
import { diagnostics } from '@/utils/diagnostics';

import { DiagnosticFormName, registerFormDiagnostics } from './formDiagnostics';
import { useNavigationFormContext } from './FormProvider';

/**
 * @description wrapper around useForm that provides common error handling, etc.
 */
export function useForm<
  TFieldValues extends FieldValues = FieldValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors RHF internal types
  TContext = any,
  TTransformedValues = TFieldValues,
>(
  args?: UseFormProps<TFieldValues, TContext, TTransformedValues>,
  diagnosticName?: DiagnosticFormName
): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  const { handleSubmit: handleSubmitUnwrapped, ...formMethods } = useFormRHF<
    TFieldValues,
    TContext,
    TTransformedValues
  >(args);

  const diagnosticRenderCount = useRef(0);
  useEffect(() => {
    if (diagnosticName) diagnosticRenderCount.current++;
  });

  const { control, subscribe } = formMethods;
  useEffect(() => {
    if (!diagnosticName) return;

    const recorder = registerFormDiagnostics(
      diagnosticName,
      // RHF has no public subscription count. Read its subject without changing it.
      () => control._subjects.state.observers.length,
      () => diagnosticRenderCount.current
    );
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ name }) => recorder.recordUpdate(name),
    });
    return () => {
      unsubscribe();
      recorder.unregister();
    };
  }, [control, diagnosticName, subscribe]);

  const { touchedFields, isSubmitting, isSubmitted, isDirty } =
    formMethods.formState;

  const isTouched = JSON.stringify(touchedFields) !== '{}';

  // Should block navigation if:
  // - Form is dirty (because we don't want to lose unsaved changes)
  // - Form is touched (because sometimes we don't set all the form's default values
  //                   on load when programatically setting the form's initial values.
  //                   So to compensate for that we require that the user has touched the
  //                   the form to enable blocking. This seems to work pretty well in practice)
  // - Form is not submitting (because we often navigate on submit)
  // - Form is not submitted  (because once submitted, we don't want to block navigation)
  const shouldBlockNavigationFromForm =
    isDirty && isTouched && !isSubmitted && !isSubmitting;

  // All forms should block navigation by default
  const { shouldBlockNavigation, setShouldBlockNavigation } =
    useOnBeforeUnloadPrompt(shouldBlockNavigationFromForm);

  const handleSubmit = useCallback<typeof handleSubmitUnwrapped>(
    (onSuccess, onError) => {
      return (event, ...rest) => {
        /**
         * NOTE: This is really tricky, and occurs in the scenario where you have two forms nested in the REACT tree,
         * even if separated by a portal they will both be submitted if using form context / form provider.
         * The reason is react events bubble up the react tree regardless of portals even if that's not the case for DOM events.
         *
         * TLDR; this stops multiple nested forms on the same page from submitting each other.
         *
         * https://reactjs.org/docs/portals.html#event-bubbling-through-portals
         * https://github.com/react-hook-form/react-hook-form/issues/2076#issuecomment-744089890
         */
        event?.stopPropagation();
        event?.preventDefault();

        return handleSubmitUnwrapped(onSuccess, onError ?? diagnostics.warn)(
          event,
          ...rest
        );
      };
    },
    [handleSubmitUnwrapped]
  );

  return {
    ...formMethods,
    handleSubmit,
    shouldBlockNavigation,
    setShouldBlockNavigation,
  };
}

/**
 * @description ALWAYS use this vs. calling reset() or performing any operations after "await mutation"
 * when saving a form. Failing to do so can lead to massive memory leaks in RHF.
 * @url https://www.react-hook-form.com/api/useform/reset/#rules
 */
export const useSubmitSuccessHandler = (fn: () => void) => {
  const {
    formState: { isSubmitSuccessful },
  } = useFormContext();

  // Callers almost always pass an inline arrow, so `fn` has a new identity on
  // every render. Keeping it in a ref lets the effect depend only on
  // `isSubmitSuccessful` while still invoking the latest callback.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // Run once per successful submit. Previously the effect also depended on
  // `fn`, so it re-fired on every render while `isSubmitSuccessful` stayed
  // true. That was masked when navigations committed synchronously and
  // unmounted the form right away, but react-router v7 defers them behind
  // React.startTransition, leaving the form mounted long enough to re-render
  // and fire again -- double-navigating, double-closing modals and repeating
  // whatever else callers do here.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!isSubmitSuccessful) {
      // Reset so a subsequent submit runs the handler again.
      hasRunRef.current = false;
      return;
    }

    if (hasRunRef.current) return;

    hasRunRef.current = true;
    fnRef.current();
  }, [isSubmitSuccessful]);

  return { isSubmitSuccessful };
};

/**
 * @description wrapper around useFormContext that includes the extended context
 */
export function useFormContext<
  TFieldValues extends FieldValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors RHF generic defaults
  TContext = any,
  TTransformedValues = TFieldValues,
>(): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  const formContext = useFormContextRHF<
    TFieldValues,
    TContext,
    TTransformedValues
  >();
  const navigationContext = useNavigationFormContext();

  return { ...formContext, ...navigationContext };
}
